import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ClientForm } from "@/components/admin/billing/client-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Users,
  Globe,
  Mail,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import type { Client } from "@db/schema";

interface ClientFormData {
  name: string;
  contactEmail?: string;
  phone?: string;
  billingAddress?: string;
  billingEmails?: string;
  vatNumber?: string;
  taxExempt: boolean;
  primaryDomain?: string;
}

export default function ClientsPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);

  const { data: clients, isLoading } = useQuery<Client[]>({
    queryKey: ["/api/billing/clients"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filteredClients = useMemo(() => {
    if (!clients) return [];
    if (!search.trim()) return clients;
    const term = search.toLowerCase();
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.contactEmail?.toLowerCase().includes(term) ||
        c.primaryDomain?.toLowerCase().includes(term),
    );
  }, [clients, search]);

  const createMutation = useMutation({
    mutationFn: async (data: ClientFormData) => {
      const payload = {
        ...data,
        billingEmails: data.billingEmails
          ? data.billingEmails
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
          : [],
      };
      const res = await apiRequest("POST", "/api/billing/clients", payload);
      return (await res.json()) as Client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/clients"] });
      setDialogOpen(false);
      toast({ title: "Client created successfully" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to create client", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ClientFormData }) => {
      const payload = {
        ...data,
        billingEmails: data.billingEmails
          ? data.billingEmails
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
          : [],
      };
      const res = await apiRequest("PATCH", `/api/billing/clients/${id}`, payload);
      return (await res.json()) as Client;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/clients"] });
      setDialogOpen(false);
      setEditingClient(null);
      toast({ title: "Client updated successfully" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to update client", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/billing/clients/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/clients"] });
      setDeleteTarget(null);
      toast({ title: "Client deleted successfully" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to delete client", description: err.message, variant: "destructive" });
    },
  });

  function handleOpenCreate() {
    setEditingClient(null);
    setDialogOpen(true);
  }

  function handleOpenEdit(client: Client) {
    setEditingClient(client);
    setDialogOpen(true);
  }

  function handleFormSubmit(data: ClientFormData) {
    if (editingClient) {
      updateMutation.mutate({ id: editingClient.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  function getFormDefaults(client: Client | null) {
    if (!client) return undefined;
    return {
      name: client.name,
      contactEmail: client.contactEmail ?? "",
      phone: client.phone ?? "",
      billingAddress: client.billingAddress ?? "",
      billingEmails: (client.billingEmails ?? []).join(", "),
      vatNumber: client.vatNumber ?? "",
      taxExempt: client.taxExempt,
      primaryDomain: client.primaryDomain ?? "",
    };
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clients"
        description="Manage your billing clients"
        action={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Client
          </Button>
        }
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[140px] rounded-lg" />
          ))}
        </div>
      ) : filteredClients.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No clients found"
          description={
            search
              ? "No clients match your search. Try a different term."
              : "Get started by adding your first client."
          }
          action={
            !search ? (
              <Button onClick={handleOpenCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Client
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredClients.map((client) => (
            <Card
              key={client.id}
              className="cursor-pointer transition-colors hover:border-primary/50"
              onClick={() => navigate(`/clients/${client.id}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-1.5 min-w-0 flex-1">
                    <h3 className="font-semibold text-base truncate">
                      {client.name}
                    </h3>
                    {client.contactEmail && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{client.contactEmail}</span>
                      </div>
                    )}
                    {client.primaryDomain && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Globe className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{client.primaryDomain}</span>
                      </div>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEdit(client);
                        }}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(client);
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingClient(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? "Edit Client" : "Add Client"}
            </DialogTitle>
            <DialogDescription>
              {editingClient
                ? "Update the client details below."
                : "Fill in the details to create a new client."}
            </DialogDescription>
          </DialogHeader>
          <ClientForm
            key={editingClient?.id ?? "new"}
            defaultValues={getFormDefaults(editingClient)}
            onSubmit={handleFormSubmit}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Client"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
